/**
 * Shared furniture for the operational boards. MASTER_PROMPT section 8.
 *
 * Recycling, ink and dispatch each answer a different question, but they
 * answer it in the same order — headline, then why, then what to walk out and
 * look at. Keeping that skeleton in one place is what stops the three drifting
 * into three different dialects of the same board.
 */

import type { ReactNode } from "react";
import type { BoardException, Kpi } from "../../lib/types";
import { fmtDate, fmtPct, fmtQty } from "../../lib/format";
import { DrillCard } from "../../components/Drill";
import { Info } from "../../components/Info";
import { PeriodPicker } from "../../components/PeriodPicker";
import { describePeriod, type Period } from "../../lib/period";
import { useCompare } from "../../lib/reportPeriod";
import { ReportDownload } from "../../components/ReportDownload";

/**
 * How many decimals a headline figure deserves.
 *
 * Quantities are whole — nobody weighs half a bag. Ratios are not: rounding
 * 2.187 to "2" hides exactly the drift the number exists to show.
 */
function formatKpi(kpi: Kpi): string {
  if (kpi.value === null) return "—";
  const n = Number(kpi.value);
  if (kpi.unit === "%") return fmtPct(kpi.value);
  if (kpi.unit === "Rs") return `₹${fmtQty(kpi.value, n < 100 ? 2 : 0)}`;
  // A unitless KPI is a ratio or an index, not a count.
  if (!kpi.unit) return Number.isInteger(n) ? fmtQty(n, 0) : n.toFixed(3);
  return fmtQty(kpi.value, 0);
}

/** Arrow as well as colour — meaning never rides on hue alone (7.5). */
function arrow(direction: string): string {
  return direction === "UP" ? "↑" : direction === "DOWN" ? "↓" : "→";
}

/**
 * `vs` names what the change is measured against — "vs Sept 2026" — because
 * with a free choice of period, "vs last month" is only sometimes true.
 */
export function KpiCard({ kpi, vs = "vs previous period" }: { kpi: Kpi; vs?: string }) {
  const value = formatKpi(kpi);

  // A card with a breakdown opens it — see `components/Drill`.
  return (
    <DrillCard drill={kpi.drill}>
      <span className="label">{kpi.label}<Info text={kpi.info} /></span>
      <span className="kpi-value num">
        {value}
        {kpi.unit && !["%", "Rs"].includes(kpi.unit) && (
          <span className="kpi__unit"> {kpi.unit}</span>
        )}
      </span>
      {/* A delta needs both a difference and something to divide by. Last
          month at nil gives an infinite change, not a 0.0% one. */}
      {kpi.delta !== null && kpi.delta_pct !== null ? (
        <>
          <span className={`kpi__delta kpi__delta--${kpi.tone.toLowerCase()}`}>
            {arrow(kpi.direction)} {fmtPct(kpi.delta_pct)} {vs}
          </span>
          {kpi.note && <span className="kpi__sub">{kpi.note}</span>}
        </>
      ) : (
        <span className="kpi__sub">
          {kpi.note || "nothing to compare against"}
        </span>
      )}
    </DrillCard>
  );
}

/**
 * The exceptions block. A board that shows a problem but does not say what to
 * do about it has only done two thirds of the job.
 */
export function Exceptions({ items }: { items: BoardException[] }) {
  if (items.length === 0) return null;
  return (
    <section className="card exceptions">
      <h3 className="section-title">Needs attention</h3>
      <ul className="exceptions__list">
        {items.map((e) => (
          <li key={e.message}>
            <span
              className={`pill pill--${
                e.severity === "CRITICAL" ? "negative" : "warning"
              }`}
            >
              {e.severity === "CRITICAL" ? "critical" : "check"}
            </span>
            {e.message}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A horizontal bar row, direct-labelled — no legend needed for one series. */
export function BarRow({
  label,
  value,
  max,
  display,
  sub,
  tone = "accent",
}: {
  label: string;
  value: number;
  max: number;
  display: string;
  sub?: string;
  tone?: "accent" | "negative";
}) {
  return (
    <div className="heat__row">
      <span className="heat__label">{label}</span>
      <div className="heat__track">
        <div
          className={`heat__bar${tone === "negative" ? " heat__bar--negative" : ""}`}
          style={{ width: `${Math.max((value / (max || 1)) * 100, 1)}%` }}
        />
      </div>
      <span className="heat__value num mono">{display}</span>
      {sub !== undefined && <span className="heat__pct num mono">{sub}</span>}
    </div>
  );
}

/**
 * A day-by-day column chart. Deliberately unlabelled per column — thirty
 * labels on a month is noise; the hover title carries the exact figure.
 */
export function DailyBars({
  rows,
  band,
}: {
  rows: { date: string; value: number | null; title: string }[];
  /** Optional acceptable range, drawn as a shaded lane behind the columns. */
  band?: { lo: number; hi: number };
}) {
  const values = rows.map((r) => r.value ?? 0);
  const max = Math.max(...values, band?.hi ?? 0, 1);

  if (rows.length === 0) {
    return <p className="section-note">Nothing recorded in this period.</p>;
  }

  return (
    <div className="daily">
      {band && (
        <div
          className="daily__band"
          style={{
            bottom: `${(band.lo / max) * 100}%`,
            height: `${((band.hi - band.lo) / max) * 100}%`,
          }}
          aria-hidden="true"
        />
      )}
      {rows.map((r) => {
        const outside =
          band && r.value !== null && (r.value < band.lo || r.value > band.hi);
        return (
          <div
            key={r.date}
            className={`daily__col${outside ? " daily__col--out" : ""}`}
            title={r.title}
          >
            <div
              className="daily__fill"
              style={{
                height: `${Math.max(((r.value ?? 0) / max) * 100, 1)}%`,
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

/** Page furniture: title, period picker, loading and error states. */
export function BoardPage({
  title,
  subtitle,
  period,
  onPeriod,
  shifts = false,
  loading,
  error,
  children,
}: {
  title: string;
  subtitle: string;
  period: Period;
  onPeriod: (value: Period) => void;
  /** Offer the shift filter — not on boards whose records have no shift. */
  shifts?: boolean;
  loading: boolean;
  error: string | null;
  children: ReactNode;
}) {
  const [compare, setCompare] = useCompare();
  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">{title}</h2>
          <p className="page__sub">
            {describePeriod(period)} · {subtitle}
          </p>
        </div>
        <div className="page__controls">
          <PeriodPicker value={period} onChange={onPeriod} shifts={shifts}
                        compare={compare} onCompare={setCompare} />
          <ReportDownload title={title} period={period} />
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}

      {loading && !error && (
        <>
          <div className="kpirow">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton kpi" style={{ height: 108 }} />
            ))}
          </div>
          <div className="skeleton state__bar" style={{ height: 220 }} />
        </>
      )}

      {!loading && children}
    </div>
  );
}

/** Dates on a board are always the plant's, never the browser's timezone. */
export function day(value: string): string {
  return fmtDate(value);
}
